import { useCallback, useEffect, useRef, useState } from "react";

import { Box, Flex, Image } from "@mantine/core";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { Text } from "components";
import { ServiceConstants } from "constant";
import { BankSelectionService } from "services";
import { Bank, BankSpecificResponse, BankSpecificStatus, BankCreditNoteResponse, NoteType, DocLanguage } from "types";

import bankNowLogo from "assets/banks/bank-now.png";
import cembraLogo from "assets/banks/cembra.png";
import cornerLogo from "assets/banks/corner.png";

import { BankTab } from "./components/bank-tab";
import { StatusBadge } from "./components/status-badge";

const {
  REQUEST_BANK_SPECIFIC_GET_ID,
  REQUEST_BANK_SPECIFIC_SAVE_ID,
  REQUEST_BANK_SPECIFIC_STATUS_CHANGE_ID,
  REQUEST_BANK_NOTES_LIST_ID,
  REQUEST_BANK_NOTE_CREATE_ID,
} = ServiceConstants;

type SelectableBank = "BANK_NOW" | "CEMBRA_MONEY_AG" | "CORNER";

const BANK_TABS: { bank: SelectableBank; labelKey: string; logo: string }[] = [
  { bank: "BANK_NOW", labelKey: "bankSelection.bank.BANK_NOW", logo: bankNowLogo },
  { bank: "CEMBRA_MONEY_AG", labelKey: "bankSelection.bank.CEMBRA_MONEY_AG", logo: cembraLogo },
  { bank: "CORNER", labelKey: "bankSelection.bank.CORNER", logo: cornerLogo },
];

type BankDataCache = {
  bankSpecific?: BankSpecificResponse;
  bankNotes: BankCreditNoteResponse[];
  processingNotes: BankCreditNoteResponse[];
  fetched: boolean;
};

const createInitialBankDataCache = (): Record<SelectableBank, BankDataCache> => ({
  BANK_NOW: { bankNotes: [], processingNotes: [], fetched: false },
  CEMBRA_MONEY_AG: { bankNotes: [], processingNotes: [], fetched: false },
  CORNER: { bankNotes: [], processingNotes: [], fetched: false },
});

const createInitialBankStatuses = (): Record<SelectableBank, BankSpecificStatus | undefined> => ({
  BANK_NOW: undefined,
  CEMBRA_MONEY_AG: undefined,
  CORNER: undefined,
});

interface BankSelectionProps {
  bankCreditRequestId: number;
  disabledBanks?: Bank[];
  isExpanded: boolean;
}

export const BankSelection = ({ bankCreditRequestId, disabledBanks = [], isExpanded }: BankSelectionProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<SelectableBank>(BANK_TABS[0].bank);
  const [bankStatuses, setBankStatuses] = useState<Record<SelectableBank, BankSpecificStatus | undefined>>(
    createInitialBankStatuses
  );
  const [bankDataCache, setBankDataCache] = useState<Record<SelectableBank, BankDataCache>>(
    createInitialBankDataCache
  );

  const fetchingRef = useRef<Set<SelectableBank>>(new Set());
  const bankDataCacheRef = useRef(bankDataCache);
  bankDataCacheRef.current = bankDataCache;

  const bankSpecificResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_SPECIFIC_GET_ID + activeTab]
  );

  const bankNotesResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_NOTES_LIST_ID + "BANK"]
  );

  const processingNotesResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_NOTES_LIST_ID + "PROCESSING"]
  );

  const noteCreateResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_NOTE_CREATE_ID]
  );

  const bankSpecificSaveResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_SPECIFIC_SAVE_ID]
  );

  const bankSpecificStatusChangeResponse = useSelector(
    (state: any) => state.service.object[REQUEST_BANK_SPECIFIC_STATUS_CHANGE_ID + activeTab]
  );

  const fetchBankData = useCallback((bank: SelectableBank) => {
    if (bankDataCacheRef.current[bank].fetched || fetchingRef.current.has(bank)) {
      return;
    }

    fetchingRef.current.add(bank);
    BankSelectionService.bankSpecificGet(bank, bankCreditRequestId);
    BankSelectionService.bankNotesAll(bankCreditRequestId, "BANK");
    BankSelectionService.bankNotesAll(bankCreditRequestId, "PROCESSING");
  }, [bankCreditRequestId]);

  useEffect(() => {
    if (isExpanded && bankCreditRequestId && !disabledBanks.includes(activeTab as Bank)) {
      fetchBankData(activeTab);
    }
  }, [isExpanded, activeTab, bankCreditRequestId, disabledBanks, fetchBankData]);

  useEffect(() => {
    if (bankSpecificResponse?.data?.id) {
      setBankDataCache((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          bankSpecific: bankSpecificResponse.data,
          fetched: true,
        },
      }));
      if (bankSpecificResponse.data.bankStatus) {
        setBankStatuses((prev) => ({
          ...prev,
          [activeTab]: bankSpecificResponse.data.bankStatus,
        }));
      }
      fetchingRef.current.delete(activeTab);
    } else if (fetchingRef.current.has(activeTab)) {
      setBankDataCache((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          fetched: true,
        },
      }));
      fetchingRef.current.delete(activeTab);
    }
  }, [bankSpecificResponse, activeTab]);

  useEffect(() => {
    if (bankNotesResponse?.status !== "ERROR" && bankNotesResponse?.data) {
      setBankDataCache((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          bankNotes: Array.isArray(bankNotesResponse.data) ? bankNotesResponse.data : [],
        },
      }));
    }
  }, [bankNotesResponse, activeTab]);

  useEffect(() => {
    if (processingNotesResponse?.status !== "ERROR" && processingNotesResponse?.data) {
      setBankDataCache((prev) => ({
        ...prev,
        [activeTab]: {
          ...prev[activeTab],
          processingNotes: Array.isArray(processingNotesResponse.data) ? processingNotesResponse.data : [],
        },
      }));
    }
  }, [processingNotesResponse, activeTab]);

  useEffect(() => {
    if (noteCreateResponse?.status !== "ERROR" && noteCreateResponse?.data?.id) {
      const noteType = noteCreateResponse.data.noteType as NoteType | undefined;
      const notesKey = noteType === "PROCESSING" ? "processingNotes" : "bankNotes";
      
      setBankDataCache((prev) => {
        const currentNotes = prev[activeTab][notesKey];
        const exists = currentNotes.some((n) => n.id === noteCreateResponse.data.id);
        if (!exists) {
          return {
            ...prev,
            [activeTab]: {
              ...prev[activeTab],
              [notesKey]: [...currentNotes, noteCreateResponse.data],
            },
          };
        }
        return prev;
      });
    }
  }, [noteCreateResponse, activeTab]);

  useEffect(() => {
    if (bankSpecificSaveResponse?.status !== "ERROR" && bankSpecificSaveResponse?.data?.id) {
      const savedBank = bankSpecificSaveResponse.data.bank as SelectableBank;
      setBankDataCache((prev) => ({
        ...prev,
        [savedBank]: {
          ...prev[savedBank],
          bankSpecific: bankSpecificSaveResponse.data,
        },
      }));
      if (bankSpecificSaveResponse.data.bankStatus) {
        setBankStatuses((prev) => ({
          ...prev,
          [savedBank]: bankSpecificSaveResponse.data.bankStatus,
        }));
      }
    }
  }, [bankSpecificSaveResponse]);

  useEffect(() => {
    if (bankSpecificStatusChangeResponse?.status !== "ERROR" && bankSpecificStatusChangeResponse?.data?.id) {
      const updatedBank = bankSpecificStatusChangeResponse.data.bank as SelectableBank;
      setBankDataCache((prev) => ({
        ...prev,
        [updatedBank]: {
          ...prev[updatedBank],
          bankSpecific: bankSpecificStatusChangeResponse.data,
        },
      }));
      if (bankSpecificStatusChangeResponse.data.bankStatus) {
        setBankStatuses((prev) => ({
          ...prev,
          [updatedBank]: bankSpecificStatusChangeResponse.data.bankStatus,
        }));
      }
    }
  }, [bankSpecificStatusChangeResponse]);

  const handleAddNote = useCallback((note: string, noteType: NoteType) => {
    BankSelectionService.bankNoteCreate({
      bankCreditRequestId,
      note,
      noteType,
    });
  }, [bankCreditRequestId]);

  const handleStatusChange = (bank: SelectableBank, status: BankSpecificStatus) => {
    const bankSpecific = bankDataCache[bank].bankSpecific;

    if (bankSpecific?.id) {
      BankSelectionService.bankSpecificChangeStatus(bankSpecific.id, status, bank);
    } else {
      BankSelectionService.bankSpecificSave({
        bank,
        bankCreditRequestId,
        bankStatus: status,
      });
    }

    setBankStatuses((prev) => ({ ...prev, [bank]: status }));
  };

  const handleSaveRequestInfo = (requestNumber: string, collaboratorName: string) => {
    const currentStatus = bankStatuses[activeTab];

    BankSelectionService.bankSpecificSave({
      bank: activeTab,
      bankCreditRequestId,
      bankStatus: currentStatus,
      requestNumber,
      collaboratorName,
    });
  };

  const handleDocLanguageChange = (language: DocLanguage) => {
    const bankSpecific = bankDataCache[activeTab].bankSpecific;
    const currentStatus = bankStatuses[activeTab];

    BankSelectionService.bankSpecificSave({
      bank: activeTab,
      bankCreditRequestId,
      bankStatus: currentStatus,
      requestNumber: bankSpecific?.requestNumber,
      collaboratorName: bankSpecific?.collaboratorName,
      docLanguage: language,
    });
  };

  const handleTabClick = (bank: SelectableBank) => {
    if (!disabledBanks.includes(bank as Bank)) {
      setActiveTab(bank);
    }
  };

  return (
    <Box
      p="md"
      style={{
        border: "1px solid rgba(50, 51, 67, 0.15)",
        borderRadius: 12,
      }}
    >
      <Flex gap="md" mb="md">
        {BANK_TABS.map(({ bank, labelKey, logo }) => {
          const isDisabled = disabledBanks.includes(bank as Bank);
          const isActive = activeTab === bank;

          return (
            <Flex
              key={bank}
              align="center"
              gap="sm"
              px="md"
              py="xs"
              onClick={() => handleTabClick(bank)}
              style={{
                border: "1px solid rgba(50, 51, 67, 0.15)",
                borderRadius: 8,
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.5 : 1,
                backgroundColor: isActive ? "rgba(50, 51, 67, 0.03)" : "transparent",
              }}
            >
              <Image src={logo} alt={t(labelKey)} w={28} h={28} fit="contain" mr={-4} />
              <Text fw={500}>{t(labelKey)}</Text>
              {!isDisabled && (
                <div onClick={(e) => e.stopPropagation()}>
                  <StatusBadge
                    status={bankStatuses[bank]}
                    onChange={(status) => handleStatusChange(bank, status)}
                  />
                </div>
              )}
            </Flex>
          );
        })}
      </Flex>

      <Box>
        <BankTab
          bank={activeTab}
          bankCreditRequestId={bankCreditRequestId}
          bankSpecific={bankDataCache[activeTab].bankSpecific}
          bankNotes={bankDataCache[activeTab].bankNotes}
          processingNotes={bankDataCache[activeTab].processingNotes}
          onAddNote={handleAddNote}
          onSaveRequestInfo={handleSaveRequestInfo}
          onDocLanguageChange={handleDocLanguageChange}
        />
      </Box>
    </Box>
  );
};
